import React, { FC } from 'react';
import { Theme, Box, Typography, makeStyles, Container, Grid } from '@material-ui/core';
import Link from 'next/link';
import { termsOfUseLinks } from 'config/routes/routes';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(5, 0, 20),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(20, 0),
    },
  },
  linksBox: {
    backgroundColor: theme.palette.common.white,
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
    padding: theme.spacing(7, 5, 4),
    position: 'sticky',
    top: 120,
  },
  link: {
    marginBottom: theme.spacing(3),
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  contentBox: {
    backgroundColor: theme.palette.common.white,
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
    padding: theme.spacing(7, 5, 4),
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 600,
  },
  sectionBox: {
    marginBottom: theme.spacing(7),
  },
}));

const TermsOfUseContent: FC = () => {
  const classes = useStyles();
  return (
    <Box className={classes.root}>
      <Container fixed>
        <Grid container spacing={5}>
          <Grid item lg={4} xs={12}>
            <Box className={classes.linksBox}>
              {termsOfUseLinks.map(({ label, route }, index) => (
                <Link href={route} key={index}>
                  <Typography className={classes.link}>{label}</Typography>
                </Link>
              ))}
            </Box>
          </Grid>
          <Grid item lg={8} xs={12}>
            <Box className={classes.contentBox}>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="terms">
                  Terms and conditions
                </Typography>
                <Typography>
                  These terms and conditions outline the rules and regulations for the use of Utilities One's Website, located at
                  www.utilitiesone.com. By accessing this Website, we assume you accept these terms and conditions. Do not
                  continue to use Utilities One if you do not agree to accept all of the terms and conditions stated on this page.
                  The following terminology applies to these Terms and Conditions, Privacy Statement and Disclaimer Notice and all
                  Agreements: "Client," "You," and "Your" refers to you, the person log on this Website and compliant to the
                  Company's terms and conditions. "The Company," "Ourselves," "We," "Our," and "Us" refers to our Company.
                  "Party," "Parties," or "Us" refers to both the Client and ourselves. All terms refer to the offer, acceptance,
                  and consideration of payment necessary to undertake the process of our assistance to the Client in the most
                  appropriate manner for the express purpose of meeting the Client's needs in respect of the provision of the
                  Company's stated services, following and subject to, prevailing law of Netherlands. Any use of the above
                  terminology or other words in the singular, plural, capitalization, and/or he/she are taken as interchangeable
                  and, therefore, referring to the same.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="cookies">
                  Cookies
                </Typography>
                <Typography>
                  We employ the use of cookies. By accessing Utilities One, you agreed to use cookies in agreement with the
                  Utilities One Privacy Policy. Most interactive websites use cookies to let us retrieve the user's details for
                  each visit. Our Website uses cookies to enable the functionality of certain areas to make it easier for people
                  visiting our Website. Some of our affiliate/advertising partners may also use cookies.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="license">
                  License
                </Typography>
                <Typography>
                  Unless otherwise stated, Utilities One and/or its licensors own the intellectual property rights for all
                  material on Utilities One. All intellectual property rights are reserved. You may access this from Utilities One
                  for your personal use, subjected to restrictions set in these terms and conditions. You must not:
                </Typography>
                <ul>
                  <li>
                    <Typography>Republish material from Utilities One</Typography>
                  </li>
                  <li>
                    <Typography>Sell, rent, or sub-license material from Utilities One</Typography>
                  </li>
                  <li>
                    <Typography>Reproduce, duplicate, or copy material from Utilities One</Typography>
                  </li>
                  <li>
                    <Typography>Redistribute content from Utilities One</Typography>
                  </li>
                  <li>
                    <Typography>This Agreement shall begin on the date hereof</Typography>
                  </li>
                </ul>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="hyperlinking">
                  Hyperlinking to our Content
                </Typography>
                <Typography>The following organizations may link to our Website without prior written approval:</Typography>
                <ul>
                  <li>
                    <Typography>Government agencies;</Typography>
                  </li>
                  <li>
                    <Typography>Search engines;</Typography>
                  </li>
                  <li>
                    <Typography>News organizations;</Typography>
                  </li>
                </ul>
                <Typography>
                  Online directory distributors may link to our Website in the same manner as they hyperlink to the Websites of
                  other listed businesses; Systemwide Accredited Businesses, except soliciting non-profit organizations, charity
                  shopping malls, and charity fundraising groups, may not hyperlink to our Website. These organizations may link
                  to our home page, to publications, or other Website information so long as the link: (a) is not in any way
                  deceptive; (b) does not falsely imply sponsorship, endorsement, or approval of the linking party and its
                  products and/or services; and (c) fits within the context of the linking party's site.
                </Typography>
                <Typography>
                  We may consider and approve other link requests from the following types of organizations:
                </Typography>
                <ul>
                  <li>
                    <Typography>commonly-known consumer and/or business information sources;</Typography>
                  </li>
                  <li>
                    <Typography>dot.com community sites;</Typography>
                  </li>
                  <li>
                    <Typography>associations or other groups representing charities;</Typography>
                  </li>
                  <li>
                    <Typography>online directory distributors;</Typography>
                  </li>
                  <li>
                    <Typography>internet portals;</Typography>
                  </li>
                  <li>
                    <Typography>accounting, law and consulting firms;</Typography>
                  </li>
                  <li>
                    <Typography>educational institutions and trade associations.</Typography>
                  </li>
                </ul>
                <Typography>
                  We will approve link requests from these organizations if we decide that: (a) the link would not make us look
                  unfavorably to ourselves or our accredited businesses; (b) the organization does not have any negative records
                  with us; (c) the benefit to us from the visibility of the hyperlink compensates the absence of Utilities One,
                  and (d) the link is in the context of general resource information.
                </Typography>
                <Typography>
                  These organizations may link to our home page so long as the link: (a) is not in any way deceptive; (b) does not
                  falsely imply sponsorship, endorsement, or approval of the linking party and its products or services; and (c)
                  fits within the context of the linking party's site.
                </Typography>
                <Typography>
                  If you are one of the organizations listed above and are interested in linking to our Website, you must inform
                  us by sending an email to Utilities One. Please include your name, your organization name, contact information
                  as well as the URL of your site, a list of any URLs from which you intend to link to our Website, and a list of
                  the URLs on our site to which you would like to link. Wait 2-3 weeks for a response.
                </Typography>
                <Typography>Approved organizations may hyperlink to our Website as follows:</Typography>
                <ul>
                  <li>
                    <Typography>By use of our corporate name; or</Typography>
                  </li>
                  <li>
                    <Typography>By use of the uniform resource locator being linked to; or</Typography>
                  </li>
                  <li>
                    <Typography>
                      By use of any other description of our Website being linked to that makes sense within the context and
                      format of content on the linking party's site.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      No use of Utilities One's logo or other artwork will be allowed for linking absent a trademark license
                      agreement.
                    </Typography>
                  </li>
                </ul>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="iFrames">
                  iFrames
                </Typography>
                <Typography>
                  Without prior approval and written permission, you may not create frames around our Web Pages that alter in any
                  way the visual presentation or appearance of our Website.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="liability">
                  Content Liability
                </Typography>
                <Typography>
                  We shall not be held responsible for any content that appears on our Website. No link(s) should appear on any
                  Website that may be interpreted as libelous, obscene, or criminal, or which infringes, otherwise violates, or
                  advocates the infringement or other violation of any third party rights.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="reservation">
                  Reservation of Rights
                </Typography>
                <Typography>
                  We reserve the right to request that you remove all links or any particular link to our Website. You approve of
                  removing all links to our Website upon request immediately. We also reserve the right to amend these terms and
                  conditions and its linking policy at any time. By continuously linking to our Website, you agree to be bound to
                  and follow these linking terms and conditions.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="removal">
                  Removal of links from our Website
                </Typography>
                <Typography>
                  If you find any link on our Website that is offensive for any reason, you are free to contact and inform us at
                  any moment. We will consider requests to remove links, but we are not obliged to respond to you directly. We do
                  not ensure that the information on this Website is correct. We do not warrant its completeness or accuracy, nor
                  do we promise to ensure that the Website remains available or that the web site's material is kept up to date.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="disclaimer">
                  Disclaimer
                </Typography>
                <Typography>
                  To the maximum extent permitted by applicable law, we exclude all representations, warranties, and conditions
                  relating to our Website and the use of this Website. Nothing in this disclaimer will:
                </Typography>
                <ul>
                  <li>
                    <Typography>limit or exclude our or your liability for death or personal injury;</Typography>
                  </li>
                  <li>
                    <Typography>limit or exclude our or your liability for fraud or fraudulent misrepresentation;</Typography>
                  </li>
                  <li>
                    <Typography>
                      modify any of our or your liabilities in any way that is not permitted under applicable law;
                    </Typography>
                  </li>
                  <li>
                    <Typography>exclude any of our or your liabilities that may not be excluded under applicable law.</Typography>
                  </li>
                </ul>
                <Typography>
                  The limitations and prohibitions of liability set in this Section and elsewhere in this disclaimer: (a) are
                  subject to the preceding paragraph; and (b) govern all liabilities arising under the disclaimer, including
                  liabilities arising in contract, in tort, and for breach of statutory duty.
                </Typography>
                <Typography>
                  As long as the Website and the information and services on the Website are provided free of charge, we will not
                  be liable for any loss or damage of any nature.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
export default TermsOfUseContent;
